import {
  BasicProgressBar,
  toBasicProgressBarBackgroundColor,
  toBasicProgressBarTextColor,
  toBasicProgressBarTheme,
} from "@playlive/overlay-core/progress-bars/basic";
import { deepSignal } from "deepsignal";
import { useEffect, useState } from "preact/hooks";
import Picker from "@/components/input/picker.input.jsx";
import DemoAnimationsDropdown from "@/components/overlays/shared/demo-animations-dropdown.jsx";
import ScaledOverlayWrapper from "@/components/overlays/wrapper/scaled-overlay-wrapper.jsx";
import useAdjustableAmounts from "@/hooks/useAdjustableAmounts.js";
import useCommon from "@/hooks/useCommon.js";
import useDemoProgressBarData from "@/hooks/useDemoProgressBarData.js";
import usePreviewAnimations from "@/hooks/usePreviewAnimations.js";
import { isYes } from "@/util/common.js";
import { CHARITY } from "@/util/const.js";
import CustomizeFullProgressBar from "./customize-full-progress-bar.jsx";

const config = deepSignal({
  charityType: CHARITY.TILTIFY,
  textColor: "light-aqua",
  totalValue: 750,
  goalValue: 1000,
  bgColor: "default-red",
  showChallenges: "yes",
  showRewards: "yes",
  showDonors: "yes",
  showScroller: "yes",
  showOriginalGoal: "no",
  donationTimeOffset: 1,
  showAllDonations: "no",
  type: "classic",
  ccamMode: "no",
  ccamFlagHorizontalPosition: "right",
  ccamFlagVerticalPosition: "bottom",
});

const useBetaProgressBars = isYes(import.meta.env.VITE_BETA_PROGRESS_BARS || "no");
const urlBase = useBetaProgressBars ? "progress-bars/basic" : "basic";

export default function BasicProgressBarPage() {
  const [showMilestones, setMilestones] = useState(false);
  const { currentAmount, goalAmount, updateAmounts, setCurrentAmount } = useAdjustableAmounts();
  const { overlayInfo, overlayURL, setConfigState, setConfigKeyValue } = useCommon({
    config,
    urlBase,
  });

  const themes = [
    { name: "Slanted", value: "slanted" },
    { name: "Classic", value: "classic" },
  ];

  const { demoDonations, demoMilestones, demoTargets, demoRewards } =
    useDemoProgressBarData(config);

  const { isPreviewingAnimation, startAnimation } = usePreviewAnimations({
    setMilestones,
    setCurrentAmount,
  });

  useEffect(() => {
    if (isYes(config.showOriginalGoal)) {
      updateAmounts({
        newCurrentAmount: 1900,
        newGoalAmount: 2000,
      });
    } else {
      updateAmounts({
        newCurrentAmount: 750,
        newGoalAmount: 1000,
      });
    }
  }, [updateAmounts]);

  const progressBarBlock = (
    <BasicProgressBar
      className={isYes(config.ccamMode) ? "" : "shadow-2xl"}
      goalAmount={goalAmount}
      originalGoalAmount={1000}
      currentAmount={currentAmount}
      textColor={toBasicProgressBarTextColor(config.textColor)}
      backgroundColor={toBasicProgressBarBackgroundColor(config.bgColor)}
      completed={false}
      ccamMode={isYes(config.ccamMode)}
      ccamFlagHorizontalPosition={config.ccamFlagHorizontalPosition}
      ccamFlagVerticalPosition={config.ccamFlagVerticalPosition}
      charityType={config.charityType}
      milestones={demoMilestones}
      donations={demoDonations}
      rewards={demoRewards}
      showOriginalGoal={isYes(config.showOriginalGoal)}
      showChallenges={isYes(config.showChallenges)}
      showRewards={isYes(config.showRewards)}
      showDonors={isYes(config.showDonors)}
      showScroller={isYes(config.showScroller)}
      demoShowMilestones={showMilestones}
      targets={demoTargets}
      type={toBasicProgressBarTheme(config.type)}
    />
  );

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col items-center justify-center  overflow-x-hidden">
        <div className="example_overlay relative w-full min-h-40">
          <ScaledOverlayWrapper
            allowScaling={false}
            defaultScale={"scale-100"}
            innerClassName="h-[220px] block"
          >
            {config.type === "classic" ? (
              progressBarBlock
            ) : (
              <div className="block">{progressBarBlock}</div>
            )}
          </ScaledOverlayWrapper>

          <DemoAnimationsDropdown onChosen={startAnimation} disabled={isPreviewingAnimation} />
        </div>

        <CustomizeFullProgressBar
          config={config}
          setConfigKeyValue={setConfigKeyValue}
          setConfigState={setConfigState}
          overlayURL={overlayURL}
          setMilestones={setMilestones}
          overlayName={overlayInfo.name}
          obsDimensions={overlayInfo.obsDimensions}
          setCurrentAmount={setCurrentAmount}
          childrenSlot="appearance"
        >
          <Picker
            onChange={setConfigState}
            currentValue={config.type}
            items={themes}
            fieldName="type"
            labelText="Theme"
          />
        </CustomizeFullProgressBar>
      </div>
    </div>
  );
}
